import React, { type FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import { compareAsc } from 'date-fns';

import type { ResendInfo } from '../types';

import { useTranslation } from 'app/lang';
import { useRemainingTimeTimer } from 'app/lib/hooks/useRemainingTimeTimer';

import { CodeError } from '../constants';

import dictionary from './CodeResendInfo.dictionary.json';

import styles from './CodeResendInfo.module.scss';

export type CodeConfirmationActionsProps = {
  changeValue: string;
  changeDataEvent?: string;
  dataGaEvent?: string;
  handleChangeDataButton: () => void;
  error: CodeError | null;
  codeResendInfo: ResendInfo;
};


function getExpirationTime(hour: number, min: number, sec: number) {
  const mins = min.toString().padStart(2, '0');
  const secs = sec.toString().padStart(2, '0');

  if (hour !== 0) {
    return `${hour}:${mins}:${secs}`;
  }

  if (min !== 0) {
    return `${min}:${secs}`;
  }

  return `${sec}`;
}

export const CodeConfirmationActions: FC<CodeConfirmationActionsProps> = ({
  dataGaEvent,
  changeValue,
  changeDataEvent,
  handleChangeDataButton,
  error,
  codeResendInfo,

}) => {
  const [delta, setDelta] = useState(0);
  const { t } = useTranslation();

  const {
    resendDate,
    serverTime,
    onChange,
    handler,
  } = codeResendInfo;

  const { hour, min, sec } = useRemainingTimeTimer(resendDate, delta);

  useEffect(() => {
    setDelta(new Date(serverTime).getTime() - Date.now());
  }, [resendDate]);

  const isExpired = compareAsc(new Date(resendDate), Date.now() + delta) <= 0;
  const expirationTime = getExpirationTime(hour, min, sec);

  const fieldError = error !== null && error !== CodeError.NOT_SEND;

  useEffect(() => {
    if (isExpired) {
      onChange();
    }
  }, [isExpired]);

  const promptClassNames = classNames(styles.fieldInfo, {
    [styles.error]: fieldError,
  });

  return (
    <>
      {isExpired || resendDate !== '' && serverTime !== ''
        && (
          <div className={styles.resendText}
               data-test="registration-codeResendInfo">
            {t(dictionary.resendText, { expirationTime })}
          </div>
        )
      }
      <div className={styles.repeatCode}>
        <button className={styles.repeatSendCode}
                type="button"
                data-ga-event={dataGaEvent}
                onClick={handler}>
          {t(dictionary.sendCode)}
        </button>

        <button className={styles.changeDataButton}
                type="button"
                onClick={handleChangeDataButton}
                data-ga-event={changeDataEvent}
                data-test="change-data-button">
          {changeValue}
        </button>

        <div className={promptClassNames}
             data-test="code-prompt">{error}
        </div>
      </div>
    </>
  );
};
